import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  message,
  Pagination,
  Select,
  Collapse,
  Checkbox,
  Spin,
  Switch,
} from "antd";
import {
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  UserOutlined,
} from "@ant-design/icons";
import useAdminData from "../hooks/useAdminData";
import {
  _get_cities_by_state_code,
  _get_support_staffs,
  _bulk_assign_business_to_officer,
} from "../../../common/axios_services";

const AdminBusinesses = ({ defaultStatus }) => {
  const {
    businesses,
    loading,
    error,
    fetchBusinesses,
    approveBusiness,
    rejectBusiness,
    updateBusinessStatus,
    updateBusinessSpecialStatus,
  } = useAdminData();

  const userProfile = useSelector((state) => state.userProfile?.user);
  const userRole = userProfile?.role?.toUpperCase() || "";
  const isAdminOrSE = ["ADMIN", "SUPPORT_EXECUTIVE"].includes(userRole);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState(
    defaultStatus ? { status: defaultStatus } : {},
  );
  const [lgas, setLgas] = useState([]);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [tempFilters, setTempFilters] = useState({});

  const [soModalVisible, setSoModalVisible] = useState(false);
  const [soModalLoading, setSoModalLoading] = useState(false);
  const [availableSOs, setAvailableSOs] = useState([]);
  const [selectedSOs, setSelectedSOs] = useState([]);
  const [approvingBusinessId, setApprovingBusinessId] = useState(null);
  const [approvingBusinessName, setApprovingBusinessName] = useState("");

  useEffect(() => {
    const fetchLgas = async () => {
      try {
        const response = await _get_cities_by_state_code("Lagos");
        if (response?.data?.data) {
          setLgas(
            response.data.data.map((lga) => ({
              label: lga.name,
              value: lga.name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching LGAs:", error);
      }
    };
    fetchLgas();
  }, []);

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "freeze", label: "Freeze" },
    { value: "blocked", label: "Blocked" },
    { value: "rejected", label: "Rejected" },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filters]);

  useEffect(() => {
    fetchBusinesses({
      page: currentPage,
      limit: pageSize,
      search: searchText,
      status: filters.status,
      lga: filters.lga,
      serviceType: filters.serviceType,
    });
  }, [
    currentPage,
    pageSize,
    searchText,
    filters.status,
    filters.lga,
    filters.serviceType,
    fetchBusinesses,
  ]);

  const handleApprove = async (businessId, businessName) => {
    openSOModal(businessId, businessName);
  };

  const handleReject = async (businessId, businessName) => {
    Modal.confirm({
      title: "Reject Business",
      content: `Are you sure you want to reject "${businessName}"? This will set the business status to "rejected".`,
      onOk: async () => {
        try {
          await rejectBusiness(businessId);
          message.success("Business rejected successfully");
        } catch (error) {
          message.error("Failed to reject business");
        }
      },
    });
  };

  const handleStatusChange = async (businessId, newStatus, businessName) => {
    Modal.confirm({
      title: "Confirm Status Change",
      content: `Are you sure you want to change the status of "${businessName}" to "${newStatus}"?`,
      onOk: async () => {
        try {
          await updateBusinessStatus(businessId, newStatus);
          message.success(`Business status updated to ${newStatus}`);
        } catch (error) {
          message.error("Failed to update business status");
        }
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSpecialStatusChange = async (
    businessId,
    isSpecial,
    businessName,
  ) => {
    try {
      await updateBusinessSpecialStatus(businessId, isSpecial);
      message.success(`Special status for "${businessName}" updated`);
    } catch (error) {
      message.error("Failed to update special status");
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleFilterOpen = () => {
    setTempFilters(filters);
    setFilterModalVisible(true);
  };

  const handleFilterApply = () => {
    setFilters(tempFilters);
    setFilterModalVisible(false);
  };

  const handleFilterClear = () => {
    setTempFilters({});
    // setFilters({});
    setFilterModalVisible(false);
  };

  const fetchAvailableSOs = async () => {
    setSoModalLoading(true);
    try {
      const response = await _get_support_staffs({
        page: 1,
        limit: 100,
        role: "support_officer",
      });

      const staffData =
        response?.data?.data?.data || response?.data?.data || [];
      if (Array.isArray(staffData)) {
        setAvailableSOs(
          staffData.map((so) => ({
            label:
              `${so.firstName || ""} ${so.lastName || ""}`.trim() ||
              so.email ||
              so.phoneNumber ||
              "Unknown",
            value: so.id,
            email: so.email,
            phone: so.phoneNumber,
            zone: so.zone?.name || "Unassigned",
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching available SOs:", error);
      message.error("Failed to load support officers");
    } finally {
      setSoModalLoading(false);
    }
  };

  const openSOModal = (businessId, businessName) => {
    setApprovingBusinessId(businessId);
    setApprovingBusinessName(businessName);
    setSelectedSOs([]);
    fetchAvailableSOs();
    setSoModalVisible(true);
  };

  const handleSOSelectionChange = (checkedValues) => {
    setSelectedSOs(checkedValues);
  };

  const handleConfirmApprovalWithSO = async () => {
    if (selectedSOs.length === 0) {
      message.warning("Please select at least one Support Officer");
      return;
    }

    setSoModalLoading(true);
    try {
      const assignments = selectedSOs.map((soId) => ({
        businessId: approvingBusinessId,
        supportOfficerId: soId,
      }));

      await _bulk_assign_business_to_officer(assignments);
      message.success("Support Officer(s) assigned successfully");

      await approveBusiness(approvingBusinessId);
      message.success("Business approved successfully");

      setSoModalVisible(false);
      setSelectedSOs([]);
      setApprovingBusinessId(null);
      setApprovingBusinessName("");
    } catch (error) {
      console.error("Error during approval process:", error);
      message.error("Failed to approve business. Please try again.");
    } finally {
      setSoModalLoading(false);
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <div className="mb-4 flex gap-2 items-center">
        <Input
          placeholder="Search businesses..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          icon={<FilterOutlined />}
          onClick={handleFilterOpen}
          type={Object.keys(filters).length > 0 ? "primary" : "default"}
          title="Filter"
        >
          Filter
        </Button>
      </div>

      <Modal
        title="Filter Businesses"
        open={filterModalVisible}
        onOk={handleFilterApply}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button key="clear" onClick={handleFilterClear}>
            Clear All
          </Button>,
          <Button key="cancel" onClick={() => setFilterModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleFilterApply}>
            Apply
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <Select
              placeholder="Select status"
              value={tempFilters.status || undefined}
              onChange={(value) =>
                setTempFilters({ ...tempFilters, status: value })
              }
              allowClear
              options={[
                { value: "pending", label: "Pending" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "freeze", label: "Freeze" },
                { value: "blocked", label: "Blocked" },
                { value: "rejected", label: "Rejected" },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">LGA</label>
            <Select
              placeholder="Select LGA"
              value={tempFilters.lga || undefined}
              onChange={(value) =>
                setTempFilters({ ...tempFilters, lga: value })
              }
              allowClear
              options={lgas}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Service Type
            </label>
            <Select
              placeholder="Select service type"
              value={tempFilters.serviceType || undefined}
              onChange={(value) =>
                setTempFilters({ ...tempFilters, serviceType: value })
              }
              allowClear
              options={[
                { value: "restaurant", label: "Restaurant" },
                { value: "store", label: "Retail" },
                { value: "raw_food", label: "Wholesale" },
              ]}
            />
          </div>
        </div>
      </Modal>

      <div className="mobile topup-data">
        <Collapse defaultActiveKey={[]}>
          {businesses?.data &&
            businesses.data.length > 0 &&
            businesses.data.map((business, index) => (
              <Collapse.Panel
                showArrow={false}
                key={index}
                header={
                  <p style={{ display: "inline" }}>
                    {business.name}&nbsp;-&nbsp;
                    <Tag
                      color={
                        business.status === "active"
                          ? "green"
                          : business.status === "pending"
                            ? "orange"
                            : "red"
                      }
                    >
                      {business.status?.toUpperCase()}
                    </Tag>
                  </p>
                }
              >
                <div>
                  <ul className="mobile-list-flex">
                    <li className="flex gap-4">
                      <span>Business Name:</span>
                      <span className="text-right">{business.name}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Owner:</span>
                      <span>
                        {`${business.owner?.firstName || ""} ${
                          business.owner?.lastName || ""
                        }`.trim() || "N/A"}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Email:</span>
                      <span>{business.owner?.email || "N/A"}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Phone:</span>
                      <span>
                        {business.owner?.phoneCode}
                        {business.owner?.phoneNumber || "N/A"}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Location:</span>
                      <span>
                        {`${business.state || ""}, ${
                          business.lga || ""
                        }`.trim() || "N/A"}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Status:</span>
                      <span>
                        <Tag
                          color={
                            business.status === "active"
                              ? "green"
                              : business.status === "pending"
                                ? "orange"
                                : "red"
                          }
                        >
                          {business.status?.toUpperCase()}
                        </Tag>
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Created At:</span>
                      <span>
                        {new Date(business.createdAt).toLocaleDateString()}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Special Business:</span>
                      <span>
                        <Switch
                          checked={business.isSpecial}
                          onChange={(checked) =>
                            handleSpecialStatusChange(
                              business.id,
                              checked,
                              business.name,
                            )
                          }
                          size="small"
                          disabled={!isAdminOrSE}
                        />
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Actions:</span>
                      <span>
                        {isAdminOrSE ? (
                          <Space>
                            <Select
                              value={business.status}
                              onChange={(value) =>
                                handleStatusChange(
                                  business.id,
                                  value,
                                  business.name,
                                )
                              }
                              options={statusOptions}
                              style={{ width: 120 }}
                              size="small"
                            />
                            {business.status !== "active" && (
                              <>
                                <Button
                                  type="primary"
                                  icon={<CheckOutlined />}
                                  onClick={() =>
                                    handleApprove(business.id, business.name)
                                  }
                                  size="small"
                                >
                                  Approve
                                </Button>
                                <Button
                                  danger
                                  icon={<CloseOutlined />}
                                  onClick={() =>
                                    handleReject(business.id, business.name)
                                  }
                                  size="small"
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </Space>
                        ) : (
                          <Tag>{business.status?.toUpperCase()}</Tag>
                        )}
                      </span>
                    </li>
                  </ul>
                </div>
              </Collapse.Panel>
            ))}
        </Collapse>
        <div style={{ marginTop: "2%" }}></div>
        <div className="mt-4 flex justify-end">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={businesses?.meta?.total || 0}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      </div>

      {/* SO Selection Modal */}
      <Modal
        title={
          <span>
            <UserOutlined /> Assign Support Officer(s) to "
            {approvingBusinessName}"
          </span>
        }
        open={soModalVisible}
        onCancel={() => {
          setSoModalVisible(false);
          setSelectedSOs([]);
          setApprovingBusinessId(null);
          setApprovingBusinessName("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSoModalVisible(false);
              setSelectedSOs([]);
              setApprovingBusinessId(null);
              setApprovingBusinessName("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="approve"
            type="primary"
            loading={soModalLoading}
            onClick={handleConfirmApprovalWithSO}
            disabled={selectedSOs.length === 0}
          >
            Assign & Approve
          </Button>,
        ]}
        width={500}
      >
        <Spin spinning={soModalLoading}>
          <div className="mb-4">
            <p className="text-gray-600">
              Please select one or more Support Officers to assign to this
              business before approval. This will help manage the business
              support.
            </p>
          </div>
          {availableSOs.length > 0 ? (
            <Checkbox.Group
              onChange={handleSOSelectionChange}
              value={selectedSOs}
              style={{ width: "100%" }}
            >
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                {availableSOs.map((so) => (
                  <Checkbox
                    key={so.value}
                    value={so.value}
                    style={{
                      display: "block",
                      margin: "8px 0",
                      padding: "8px",
                      border: "1px solid #f0f0f0",
                      borderRadius: "4px",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{so.label}</div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          marginTop: "2px",
                        }}
                      >
                        {so.email && <span>{so.email}</span>}
                        {so.email && so.phone && <span> | </span>}
                        {so.phone && <span>{so.phone}</span>}
                        <span> | Zone: {so.zone}</span>
                      </div>
                    </div>
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>No available Support Officers found.</p>
              <p style={{ fontSize: "12px", color: "#888" }}>
                Please create Support Officers first before approving
                businesses.
              </p>
            </div>
          )}
          {selectedSOs.length > 0 && (
            <div style={{ marginTop: "12px", textAlign: "center" }}>
              <Tag color="blue">
                {selectedSOs.length} Support Officer(s) selected
              </Tag>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default AdminBusinesses;
